import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Phone, FileText, CheckCircle, Loader2, Upload, 
  ChevronRight, Building2, Shield, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

/**
 * Multi-step Claim Business Wizard
 * Steps:
 * 1. Initial claim submission (verification method selection)
 * 2. Phone verification (if applicable)
 * 3. Document upload (business license, utility bill, etc.)
 * 4. Review & submit
 */
export default function ClaimBusinessWizard({ business, isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [claimData, setClaimData] = useState({
    verification_method: '',
    verification_details: '',
    contact_phone: '',
    document_url: '',
    document_type: ''
  });
  const [phoneVerification, setPhoneVerification] = useState({
    sent: false,
    code: '',
    verified: false
  });
  const [errors, setErrors] = useState({});

  const steps = [
    { number: 1, label: 'Claim Details', icon: Building2 },
    { number: 2, label: 'Phone Verify', icon: Phone },
    { number: 3, label: 'Documents', icon: FileText },
    { number: 4, label: 'Review', icon: Shield }
  ];

  const verificationMethods = [
    { value: 'phone', label: 'Phone Verification', description: 'Verify using the business phone number', icon: Phone },
    { value: 'document', label: 'Document Proof', description: 'Upload business license or utility bill', icon: FileText },
    { value: 'owner_statement', label: 'Owner Statement', description: 'Provide a written statement of ownership', icon: Building2 }
  ];

  const documentTypes = [
    { value: 'business_license', label: 'Business License' },
    { value: 'utility_bill', label: 'Utility Bill' },
    { value: 'tax_document', label: 'Tax Document' },
    { value: 'other', label: 'Other Official Document' }
  ];

  const handleInitialSubmit = async () => {
    if (!claimData.verification_method) {
      setErrors({ verification_method: 'Please select a verification method' });
      return;
    }
    if (!claimData.verification_details) {
      setErrors({ verification_details: 'Please provide verification details' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/claims`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: business.business_id,
          verification_method: claimData.verification_method,
          verification_details: claimData.verification_details,
          contact_phone: claimData.contact_phone
        })
      });

      if (response.ok) {
        const data = await response.json();
        setClaimId(data.claim_id);
        
        // Skip phone step if not phone verification
        if (claimData.verification_method === 'phone') {
          setStep(2);
        } else {
          setStep(3);
        }
        toast.success('Claim submitted! Continue with verification.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit claim');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPhoneCode = async () => {
    if (!claimData.contact_phone || claimData.contact_phone.length < 10) {
      setErrors({ contact_phone: 'Please enter a valid phone number' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/claims/${claimId}/request-phone-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          claim_id: claimId,
          phone_number: claimData.contact_phone
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPhoneVerification(prev => ({ ...prev, sent: true }));
        toast.success('Verification code sent!');
        
        // For development, show the code
        if (data.debug_code) {
          console.log('Debug code:', data.debug_code);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send verification code');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneVerification.code || phoneVerification.code.length !== 6) {
      setErrors({ code: 'Please enter the 6-digit code' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/claims/${claimId}/verify-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          claim_id: claimId,
          code: phoneVerification.code
        })
      });

      if (response.ok) {
        setPhoneVerification(prev => ({ ...prev, verified: true }));
        toast.success('Phone verified!');
        setStep(3);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!claimData.document_url) {
      // Skip document step if URL not provided
      setStep(4);
      return;
    }

    if (!claimData.document_type) {
      setErrors({ document_type: 'Please select a document type' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/claims/${claimId}/upload-document?document_url=${encodeURIComponent(claimData.document_url)}&document_type=${claimData.document_type}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document uploaded!');
        setStep(4);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = () => {
    toast.success('Claim submitted for review! We\'ll notify you once it\'s processed.');
    onSuccess?.();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl bg-[#0a0a0a] border border-zinc-800 rounded-xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Claim This Business</h2>
              <p className="text-sm text-zinc-500">{business?.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-zinc-800 transition-colors"
              data-testid="close-claim-modal"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              {steps.map((s, idx) => (
                <div key={s.number} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    step >= s.number ? 'text-white' : 'text-zinc-600'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      step > s.number 
                        ? 'bg-green-500' 
                        : step === s.number 
                        ? 'bg-white text-black' 
                        : 'bg-zinc-800'
                    }`}>
                      {step > s.number ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <s.icon className="w-4 h-4" />
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{s.label}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <ChevronRight className={`w-4 h-4 mx-2 ${
                      step > s.number ? 'text-green-500' : 'text-zinc-700'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Step 1: Claim Details */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-zinc-300 block mb-3">
                    How would you like to verify your ownership?
                  </label>
                  <div className="space-y-3">
                    {verificationMethods.map((method) => (
                      <button
                        key={method.value}
                        onClick={() => {
                          setClaimData(prev => ({ ...prev, verification_method: method.value }));
                          setErrors({});
                        }}
                        className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                          claimData.verification_method === method.value
                            ? 'border-white bg-white/5'
                            : 'border-zinc-800 hover:border-zinc-700'
                        }`}
                        data-testid={`method-${method.value}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          claimData.verification_method === method.value
                            ? 'bg-white text-black'
                            : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          <method.icon className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-medium">{method.label}</p>
                          <p className="text-zinc-500 text-sm">{method.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {errors.verification_method && (
                    <p className="text-red-400 text-sm mt-2">{errors.verification_method}</p>
                  )}
                </div>

                {claimData.verification_method === 'phone' && (
                  <div>
                    <label className="text-sm font-medium text-zinc-300 block mb-2">
                      Business Phone Number
                    </label>
                    <input
                      type="tel"
                      value={claimData.contact_phone}
                      onChange={(e) => setClaimData(prev => ({ ...prev, contact_phone: e.target.value }))}
                      placeholder="(555) 555-5555"
                      className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                      data-testid="phone-input"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-zinc-300 block mb-2">
                    Additional Details
                  </label>
                  <textarea
                    value={claimData.verification_details}
                    onChange={(e) => {
                      setClaimData(prev => ({ ...prev, verification_details: e.target.value }));
                      setErrors({});
                    }}
                    placeholder="Tell us about your relationship to this business..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 resize-none"
                    data-testid="details-textarea"
                  />
                  {errors.verification_details && (
                    <p className="text-red-400 text-sm mt-1">{errors.verification_details}</p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Phone Verification */}
            {step === 2 && (
              <div className="space-y-6">
                {!phoneVerification.sent ? (
                  <>
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                        <Phone className="w-8 h-8 text-blue-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Verify Your Phone</h3>
                      <p className="text-zinc-400">
                        We'll send a 6-digit code to verify you have access to the business phone.
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-zinc-300 block mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={claimData.contact_phone}
                        onChange={(e) => setClaimData(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="(555) 555-5555"
                        className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                      />
                      {errors.contact_phone && (
                        <p className="text-red-400 text-sm mt-1">{errors.contact_phone}</p>
                      )}
                    </div>

                    <button
                      onClick={handleRequestPhoneCode}
                      disabled={loading}
                      className="w-full py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Verification Code'}
                    </button>
                  </>
                ) : !phoneVerification.verified ? (
                  <>
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Enter Verification Code</h3>
                      <p className="text-zinc-400">
                        We sent a code to {claimData.contact_phone?.slice(0, 3)}****{claimData.contact_phone?.slice(-4)}
                      </p>
                    </div>

                    <div>
                      <input
                        type="text"
                        value={phoneVerification.code}
                        onChange={(e) => setPhoneVerification(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        placeholder="000000"
                        maxLength={6}
                        className="w-full px-4 py-4 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                        data-testid="verification-code-input"
                      />
                      {errors.code && (
                        <p className="text-red-400 text-sm mt-1 text-center">{errors.code}</p>
                      )}
                    </div>

                    <button
                      onClick={handleVerifyPhone}
                      disabled={loading || phoneVerification.code.length !== 6}
                      className="w-full py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify Code'}
                    </button>

                    <button
                      onClick={() => setPhoneVerification(prev => ({ ...prev, sent: false }))}
                      className="w-full text-center text-zinc-400 text-sm hover:text-white"
                    >
                      Didn't receive code? Try again
                    </button>
                  </>
                ) : null}

                {/* Skip button */}
                <button
                  onClick={() => setStep(3)}
                  className="w-full text-center text-zinc-500 text-sm hover:text-white"
                >
                  Skip this step
                </button>
              </div>
            )}

            {/* Step 3: Document Upload */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Upload Supporting Documents</h3>
                  <p className="text-zinc-400">
                    Provide proof of ownership to speed up your claim approval.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-zinc-300 block mb-2">
                    Document Type
                  </label>
                  <select
                    value={claimData.document_type}
                    onChange={(e) => setClaimData(prev => ({ ...prev, document_type: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:border-zinc-600"
                    data-testid="document-type-select"
                  >
                    <option value="">Select document type...</option>
                    {documentTypes.map(doc => (
                      <option key={doc.value} value={doc.value}>{doc.label}</option>
                    ))}
                  </select>
                  {errors.document_type && (
                    <p className="text-red-400 text-sm mt-1">{errors.document_type}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-zinc-300 block mb-2">
                    Document URL or Upload
                  </label>
                  <input
                    type="url"
                    value={claimData.document_url}
                    onChange={(e) => setClaimData(prev => ({ ...prev, document_url: e.target.value }))}
                    placeholder="https://... or upload below"
                    className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
                    data-testid="document-url-input"
                  />
                  <p className="text-xs text-zinc-600 mt-1">
                    Tip: Upload your document to Google Drive or Dropbox and paste the link
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(4)}
                    className="flex-1 py-3 rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleDocumentUpload}
                    disabled={loading}
                    className="flex-1 py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Review Your Claim</h3>
                  <p className="text-zinc-400">
                    Please review your claim details before submitting.
                  </p>
                </div>

                <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Business</span>
                    <span className="text-white">{business?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Verification Method</span>
                    <span className="text-white capitalize">{claimData.verification_method?.replace('_', ' ')}</span>
                  </div>
                  {claimData.contact_phone && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Phone</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white">{claimData.contact_phone}</span>
                        {phoneVerification.verified && (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                    </div>
                  )}
                  {claimData.document_type && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Document</span>
                      <span className="text-white capitalize">{claimData.document_type?.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex gap-3">
                  <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 text-sm font-medium">Review Timeline</p>
                    <p className="text-yellow-400/70 text-sm">
                      Claims are typically reviewed within 2-3 business days. You'll receive a notification once your claim is processed.
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleFinalSubmit}
                  className="w-full py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                  data-testid="submit-claim-btn"
                >
                  Submit Claim for Review
                </button>
              </div>
            )}
          </div>

          {/* Footer Navigation */}
          {step > 1 && step < 4 && (
            <div className="px-6 py-4 border-t border-zinc-800">
              <button
                onClick={() => setStep(step - 1)}
                className="text-zinc-400 text-sm hover:text-white transition-colors"
              >
                ← Back to previous step
              </button>
            </div>
          )}

          {/* Initial Submit Button */}
          {step === 1 && (
            <div className="px-6 py-4 border-t border-zinc-800">
              <button
                onClick={handleInitialSubmit}
                disabled={loading}
                className="w-full py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                data-testid="start-claim-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Start Claim Process'}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
